import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/layout/Layout";
import { listAssets, listClients, listSites, createAsset, updateAsset } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Wind, Cog, Building, QrCode, Pencil, Power } from "lucide-react";
import { PriorityPill } from "@/components/Pills";
import { toast } from "sonner";
import AssetQrDialog from "@/components/AssetQrDialog";

const CATS = [
  { key: "all", label: "All", icon: Cog },
  { key: "hvac", label: "HVAC", icon: Wind },
  { key: "equipment", label: "Equipment", icon: Cog },
  { key: "civil", label: "Civil Work", icon: Building },
];

export default function Assets() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [qrAsset, setQrAsset] = useState(null);
  const [editAsset, setEditAsset] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => setAssets(await listAssets({ include_inactive: showInactive }));
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [showInactive]);

  const toggleActive = async (a) => {
    const next = a.active === false ? true : false;
    if (!next && !window.confirm(`Deactivate "${a.name}"?`)) return;
    await updateAsset(a.id, { active: next });
    toast.success(next ? "Asset activated" : "Asset deactivated");
    load();
  };

  const filtered = useMemo(() => {
    return assets.filter((a) => {
      if (cat !== "all" && a.category !== cat) return false;
      if (q && !`${a.tag} ${a.name} ${a.manufacturer || ""}`.toLowerCase().includes(q.toLowerCase()))
        return false;
      return true;
    });
  }, [assets, cat, q]);

  const canCreate = user?.role === "admin";

  return (
    <Layout
      title="Assets"
      breadcrumbs={["Operations", "Assets Registry"]}
      actions={
        canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-asset-btn"
                className="bg-orange-500 hover:bg-orange-600 rounded-sm font-semibold"
              >
                <Plus className="h-4 w-4 mr-1" /> New Asset
              </Button>
            </DialogTrigger>
            <NewAssetDialog onCreated={() => { setOpen(false); load(); }} />
          </Dialog>
        )
      }
    >
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            data-testid="asset-search"
            placeholder="Search by tag, name, manufacturer…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-sm bg-white border-zinc-300"
          />
        </div>
        <div className="flex gap-1 bg-white border border-zinc-200 rounded-sm p-1">
          {CATS.map((c) => (
            <button
              key={c.key}
              data-testid={`asset-cat-${c.key}`}
              onClick={() => setCat(c.key)}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm flex items-center gap-1 ${
                cat === c.key ? "bg-orange-500 text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <c.icon className="h-3.5 w-3.5" /> {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((a) => (
          <AssetCard
            key={a.id}
            a={a}
            onQr={() => setQrAsset(a)}
            onEdit={user?.role === "admin" ? () => setEditAsset(a) : null}
            onToggleActive={user?.role === "admin" ? () => toggleActive(a) : null}
          />
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-12 text-zinc-500 text-sm">No assets match your filters.</div>
      )}
      <AssetQrDialog asset={qrAsset} open={!!qrAsset} onOpenChange={(o) => !o && setQrAsset(null)} />
      <Dialog open={!!editAsset} onOpenChange={(o) => !o && setEditAsset(null)}>
        {editAsset && <EditAssetDialog asset={editAsset} onSaved={() => { setEditAsset(null); load(); }} />}
      </Dialog>
      {user?.role === "admin" && (
        <div className="mt-4 text-xs text-zinc-500">
          <label className="inline-flex items-center gap-2">
            <input
              data-testid="assets-show-inactive"
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive assets
          </label>
        </div>
      )}
    </Layout>
  );
}

const AssetCard = ({ a, onQr, onEdit, onToggleActive }) => {
  const health = a.health;
  const color = health > 85 ? "bg-emerald-500" : health > 70 ? "bg-orange-500" : "bg-red-500";
  return (
    <div className={`engineered-card overflow-hidden ${a.active === false ? "opacity-60" : ""}`} data-testid={`asset-card-${a.tag}`}>
      <div className="h-32 bg-zinc-100 relative">
        {a.image_url && (
          <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
        )}
        <div className="absolute top-2 left-2 font-mono text-[10px] font-bold bg-zinc-950 text-white px-2 py-0.5 rounded-sm">
          {a.tag}
        </div>
        {a.active === false && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 font-mono text-[10px] font-bold bg-red-600 text-white px-2 py-0.5 rounded-sm">
            INACTIVE
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-1">
          {onEdit && (
            <button
              data-testid={`edit-asset-${a.tag}`}
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="h-7 w-7 rounded-sm bg-white/95 hover:bg-orange-500 hover:text-white text-zinc-700 flex items-center justify-center border border-zinc-200"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {onToggleActive && (
            <button
              data-testid={`toggle-asset-${a.tag}`}
              onClick={(e) => { e.stopPropagation(); onToggleActive(); }}
              className={`h-7 w-7 rounded-sm flex items-center justify-center border ${a.active === false ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-white/95 border-zinc-200 text-zinc-700 hover:bg-red-50 hover:text-red-700"}`}
              title={a.active === false ? "Activate" : "Deactivate"}
            >
              <Power className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            data-testid={`qr-btn-${a.tag}`}
            onClick={(e) => { e.stopPropagation(); onQr(); }}
            className="h-7 w-7 rounded-sm bg-white/95 hover:bg-orange-500 hover:text-white text-zinc-700 flex items-center justify-center border border-zinc-200"
            title="QR / Print label"
          >
            <QrCode className="h-3.5 w-3.5" />
          </button>
          <PriorityPill priority={a.criticality} />
        </div>
      </div>
      <div className="p-4">
        <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">
          {a.category} · {a.subcategory}
        </div>
        <h3 className="font-display font-bold text-lg text-zinc-950">{a.name}</h3>
        <div className="text-xs text-zinc-600 mt-1 font-mono">
          {a.manufacturer || "—"} {a.model ? `· ${a.model}` : ""}
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">
            <span>Health</span>
            <span className="font-mono">{health}%</span>
          </div>
          <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
            <div className={`h-full ${color}`} style={{ width: `${health}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
};

function NewAssetDialog({ onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("hvac");
  const [subcategory, setSubcategory] = useState("");
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [criticality, setCriticality] = useState("medium");
  const [manufacturer, setManufacturer] = useState("");
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listClients().then(setClients);
  }, []);
  useEffect(() => {
    if (clientId) {
      listSites(clientId).then(setSites);
      setSiteId("");
    }
  }, [clientId]);

  const submit = async () => {
    if (!name || !clientId || !siteId) {
      toast.error("Name, client and site are required");
      return;
    }
    setBusy(true);
    try {
      await createAsset({
        name,
        category,
        subcategory,
        client_id: clientId,
        site_id: siteId,
        criticality,
        manufacturer,
      });
      toast.success("Asset created");
      onCreated();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Register New Asset</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
          <Input data-testid="asset-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm h-11" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="asset-category" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hvac">HVAC</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
              <SelectItem value="civil">Civil Work</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Subcategory</Label>
          <Input data-testid="asset-sub" value={subcategory} onChange={(e) => setSubcategory(e.target.value)} className="rounded-sm h-11" placeholder="e.g. Chiller" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Client</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger data-testid="asset-client" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Site</Label>
          <Select value={siteId} onValueChange={setSiteId} disabled={!clientId}>
            <SelectTrigger data-testid="asset-site" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Criticality</Label>
          <Select value={criticality} onValueChange={setCriticality}>
            <SelectTrigger data-testid="asset-crit" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Manufacturer</Label>
          <Input data-testid="asset-mfr" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} className="rounded-sm h-11" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy} data-testid="asset-submit" className="rounded-sm bg-orange-500 hover:bg-orange-600 font-bold">
          {busy ? "Creating…" : "Create Asset"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditAssetDialog({ asset, onSaved }) {
  const [form, setForm] = useState({
    name: asset.name || "",
    tag: asset.tag || "",
    category: asset.category,
    subcategory: asset.subcategory || "",
    criticality: asset.criticality || "medium",
    manufacturer: asset.manufacturer || "",
    model: asset.model || "",
    image_url: asset.image_url || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    setBusy(true);
    try {
      await updateAsset(asset.id, form);
      toast.success("Asset updated");
      onSaved();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Edit Asset · {asset.tag}</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
          <Input data-testid="edit-asset-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Tag</Label>
          <Input data-testid="edit-asset-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Subcategory</Label>
          <Input data-testid="edit-asset-sub" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="rounded-sm h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hvac">HVAC</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
              <SelectItem value="civil">Civil Work</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Criticality</Label>
          <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })}>
            <SelectTrigger className="rounded-sm h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Manufacturer</Label>
          <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Model</Label>
          <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Image URL</Label>
          <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-sm h-11" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy} data-testid="edit-asset-submit" className="rounded-sm bg-orange-500 hover:bg-orange-600 font-bold">
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
